if (!customElements.get('product-switcher')) {
  class ProductSwitcher extends HTMLElement {
    galleries = new Map();

    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-switcher-tab]')];
      this.panels = [...this.querySelectorAll('[data-switcher-panel]')];

      this.onTabClick = this.handleTabClick.bind(this);
      this.onTabKeydown = this.handleTabKeydown.bind(this);
      this.onChange = this.handleChange.bind(this);
      this.onClick = this.handleClick.bind(this);
      this.onDocumentClick = this.handleDocumentClick.bind(this);
      this.onDocumentKeydown = this.handleDocumentKeydown.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onClick);
      document.addEventListener('click', this.onDocumentClick);
      document.addEventListener('keydown', this.onDocumentKeydown);

      for (const tab of this.tabs) {
        tab.addEventListener('click', this.onTabClick);
        tab.addEventListener('keydown', this.onTabKeydown);
      }

      for (const purchase of this.querySelectorAll('[data-switcher-purchase]')) {
        this.updatePurchase(purchase);
      }

      const activeGallery = this.querySelector('[data-switcher-gallery]:not([hidden])');
      if (activeGallery) this.initGallery(activeGallery);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('click', this.onDocumentClick);
      document.removeEventListener('keydown', this.onDocumentKeydown);

      for (const tab of this.tabs ?? []) {
        tab.removeEventListener('click', this.onTabClick);
        tab.removeEventListener('keydown', this.onTabKeydown);
      }

      for (const { main, thumbs } of this.galleries.values()) {
        main.destroy();
        thumbs?.destroy();
      }
      this.galleries.clear();
    }

    /**
     * Shows every part (media, header, panel) that belongs to the given tab key.
     * @param {string} key - 'combo', 'face' or 'body'
     * @returns {void}
     */
    select(key) {
      for (const tab of this.tabs) {
        const isSelected = tab.dataset.switcherTab === key;
        tab.setAttribute('aria-selected', String(isSelected));
        tab.tabIndex = isSelected ? 0 : -1;
      }

      for (const panel of this.panels) {
        panel.hidden = panel.dataset.switcherPanel !== key;
      }

      for (const gallery of this.querySelectorAll('[data-switcher-gallery]')) {
        if (gallery.hidden) {
          this.pauseVideos(gallery);
        } else {
          this.initGallery(gallery);
        }
      }

      this.dispatchEvent(new CustomEvent('product-switcher:change', { bubbles: true, detail: { key } }));
    }

    /**
     * Starts Swiper for a gallery the first time it is shown, and re-measures it on later shows.
     * Mirrors the PDP gallery set-up in component-product-info.js initSwiper().
     * @param {HTMLElement} gallery
     * @returns {void}
     */
    initGallery(gallery) {
      const existing = this.galleries.get(gallery);

      if (existing) {
        existing.main.update();
        existing.thumbs?.update();
        return;
      }

      const mainContainer = gallery.querySelector('.product-switcher-gallery__main');
      if (!mainContainer || Number(mainContainer.dataset.slideCount) < 2 || typeof Swiper === 'undefined') return;

      const thumbsContainer = gallery.querySelector('.product-switcher-gallery__thumbs');
      const thumbs = thumbsContainer
        ? new Swiper(thumbsContainer, {
            slidesPerView: 'auto',
            spaceBetween: 12,
            freeMode: true,
            watchSlidesProgress: true,
          })
        : null;

      const main = new Swiper(mainContainer, {
        autoHeight: true,
        slidesPerView: 1.1,
        spaceBetween: 8,
        navigation: {
          prevEl: mainContainer.querySelector('.swiper-button-prev'),
          nextEl: mainContainer.querySelector('.swiper-button-next'),
        },
        pagination: {
          el: mainContainer.querySelector('.swiper-pagination'),
          clickable: true,
        },
        breakpoints: {
          750: { slidesPerView: 1, spaceBetween: 0 },
        },
        thumbs: thumbs ? { swiper: thumbs } : undefined,
      });

      main.on('slideChange', () => {
        this.pauseVideos(mainContainer);
        thumbs?.slideTo(main.activeIndex);
      });

      this.galleries.set(gallery, { main, thumbs });
    }

    /**
     * @param {HTMLElement} container
     * @returns {void}
     */
    pauseVideos(container) {
      for (const video of container.querySelectorAll('video')) {
        if (!video.paused) video.pause();
      }
    }

    /**
     * @param {Event} event
     * @returns {void}
     */
    handleChange(event) {
      const purchase = event.target.closest('[data-switcher-purchase]');
      if (purchase && event.target.matches('.bundle-purchase-card__radio-input')) this.updatePurchase(purchase);
    }

    /**
     * Frequency options and tooltip triggers are buttons inside the card <label>, which a
     * label does not activate, so they are handled here.
     * @param {MouseEvent} event
     * @returns {void}
     */
    handleClick(event) {
      const addButton = event.target.closest('[data-switcher-add]');
      if (addButton) {
        this.addToCart(addButton);
        return;
      }

      const frequencyOption = event.target.closest('[data-frequency-option]');
      if (frequencyOption) {
        this.selectFrequency(frequencyOption);
        return;
      }

      const drawerOpener = event.target.closest('[data-info-drawer-open]');
      if (drawerOpener) {
        // section-bundle-product.js opens the drawer on this same click; move focus in once it has.
        this.drawerOpener = drawerOpener;
        requestAnimationFrame(() => {
          document.getElementById(drawerOpener.dataset.infoDrawerOpen)?.querySelector('.info-drawer__close')?.focus();
        });
        return;
      }

      const tooltipTrigger = event.target.closest('[data-switcher-tooltip-trigger]');
      if (tooltipTrigger) {
        event.preventDefault();
        const wrap = tooltipTrigger.closest('[data-switcher-tooltip]');
        const isOpen = !wrap.classList.contains('is-open');
        this.closeTooltips();
        wrap.classList.toggle('is-open', isOpen);
        tooltipTrigger.setAttribute('aria-expanded', String(isOpen));
      }
    }

    handleDocumentClick(event) {
      const selectTrigger = event.target.closest('[data-switcher-select]');
      if (selectTrigger && !this.contains(selectTrigger) && document.querySelector('product-switcher') === this) {
        event.preventDefault();
        this.reveal(selectTrigger.dataset.switcherSelect);
        return;
      }

      if (!event.target.closest('[data-switcher-tooltip]')) this.closeTooltips();
      if (event.target.closest('[data-info-drawer-close]')) this.restoreDrawerFocus();
    }

    handleDocumentKeydown(event) {
      if (event.key !== 'Escape') return;

      this.closeTooltips();
      this.restoreDrawerFocus();
    }

    /**
     * Handles [data-switcher-select] buttons elsewhere on the page (hero, benefits, CTA banner,
     * sticky bar): selects the requested tab, scrolls here and moves focus to that tab.
     * The tab switch is attempted separately so a failure there still scrolls the shopper here.
     * @param {string} target - 'combo', 'single' (first single-product tab) or a tab key
     * @returns {void}
     */
    reveal(target) {
      const key =
        target === 'single'
          ? this.tabs.find((tab) => tab.dataset.switcherTab !== 'combo')?.dataset.switcherTab
          : target;

      try {
        if (this.tabs.some((tab) => tab.dataset.switcherTab === key)) this.select(key);
      } catch (error) {
        console.error('product-switcher: could not select tab', key, error);
      }

      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.scrollIntoView({ behavior: prefersReducedMotion ? 'auto' : 'smooth', block: 'start' });

      const focusTarget = this.querySelector('[role="tab"][aria-selected="true"]');
      focusTarget?.focus({ preventScroll: true });
    }

    /**
     * Returns focus to the trust link that opened an info drawer, once that drawer has closed.
     * @returns {void}
     */
    restoreDrawerFocus() {
      const opener = this.drawerOpener;
      if (!opener) return;

      requestAnimationFrame(() => {
        if (document.getElementById(opener.dataset.infoDrawerOpen)?.classList.contains('is-open')) return;

        this.drawerOpener = null;
        opener.focus();
      });
    }

    closeTooltips() {
      for (const wrap of this.querySelectorAll('[data-switcher-tooltip].is-open')) {
        wrap.classList.remove('is-open');
        wrap.querySelector('[data-switcher-tooltip-trigger]')?.setAttribute('aria-expanded', 'false');
      }
    }

    /**
     * Adds the tab's product, or both products for the combined tab, as separate cart lines.
     * Each line carries the selected selling plan when the subscribe card is chosen; any bundle
     * discount is applied by the store's automatic discount, not here.
     * @param {HTMLButtonElement} button
     * @returns {void}
     */
    addToCart(button) {
      if (button.disabled || !window.liquidAjaxCart) return;

      const panel = button.closest('[data-switcher-panel]');
      const error = panel.querySelector('[data-switcher-error]');
      const sellingPlanId = panel.querySelector('[data-switcher-purchase]')?.dataset.sellingPlanId;
      const items = button.dataset.variantIds.split(',').map((id) => ({
        id: Number(id),
        quantity: 1,
        ...(sellingPlanId ? { selling_plan: Number(sellingPlanId) } : {}),
      }));

      error.textContent = '';
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');

      window.liquidAjaxCart.add(
        { items },
        {
          lastCallback: (requestState) => {
            button.disabled = false;
            button.removeAttribute('aria-busy');

            const response = requestState.responseData;
            if (response?.ok) {
              // The PDP opens the cart drawer from <product-info>, which only does so on /products/ pages.
              document.dispatchEvent(new CustomEvent('item-added-to-cart', { detail: response.body }));
              return;
            }

            error.textContent = response?.body?.description || response?.body?.message || this.dataset.cartError;
          },
        }
      );
    }

    /**
     * Marks a delivery frequency as chosen, copies its plan and prices onto the subscribe
     * radio and selects the subscribe card.
     * @param {HTMLButtonElement} option
     * @returns {void}
     */
    selectFrequency(option) {
      const purchase = option.closest('[data-switcher-purchase]');
      const subscribeRadio = purchase.querySelector('.bundle-purchase-card__radio-input[value="subscribe"]');

      for (const sibling of option.parentElement.querySelectorAll('[data-frequency-option]')) {
        const isSelected = sibling === option;
        sibling.classList.toggle('is-selected', isSelected);
        sibling.setAttribute('aria-checked', String(isSelected));
      }

      Object.assign(subscribeRadio.dataset, {
        sellingPlanId: option.dataset.sellingPlanId,
        price: option.dataset.price,
        comparePrice: option.dataset.comparePrice,
        saveLabel: option.dataset.saveLabel ?? '',
      });

      for (const perk of purchase.querySelectorAll('[data-delivery-perk]')) {
        perk.textContent = perk.dataset.perkTemplate.replace('[frequency]', option.dataset.frequency);
      }

      for (const perk of purchase.querySelectorAll('[data-per-month-perk]')) {
        perk.textContent = perk.dataset.perkTemplate.replace('[per_month]', option.dataset.perMonth);
      }

      const cardPrice = subscribeRadio.closest('.bundle-purchase-card').querySelector('[data-card-price]');
      this.renderPrice(cardPrice, '.bundle-purchase-card__price-compare', '.bundle-purchase-card__price-current', option.dataset);

      subscribeRadio.checked = true;
      this.updatePurchase(purchase);
    }

    /**
     * Syncs the active card, the plan id the add-to-cart will send, and the header price
     * with the checked radio.
     * @param {HTMLElement} purchase
     * @returns {void}
     */
    updatePurchase(purchase) {
      const checked = purchase.querySelector('.bundle-purchase-card__radio-input:checked');
      if (!checked) return;

      for (const card of purchase.querySelectorAll('.bundle-purchase-card')) {
        card.classList.toggle('is-active', card.contains(checked));
      }

      purchase.dataset.sellingPlanId = checked.dataset.sellingPlanId;

      const key = purchase.closest('[data-switcher-panel]').dataset.switcherPanel;
      const header = this.querySelector(`.product-switcher__header[data-switcher-panel="${key}"]`);
      if (!header) return;

      const compare = header.querySelector('[data-switcher-compare-price]');
      const showCompare = checked.dataset.comparePrice && checked.dataset.comparePrice !== checked.dataset.price;
      compare.hidden = !showCompare;
      compare.querySelector('[data-switcher-price-value]').textContent = checked.dataset.comparePrice;
      header.querySelector('[data-switcher-current-price]').textContent = checked.dataset.price;

      const saveBadge = header.querySelector('[data-switcher-save-badge]');
      if (saveBadge) {
        saveBadge.textContent = checked.dataset.saveLabel ?? '';
        saveBadge.hidden = !saveBadge.textContent;
      }
    }

    /**
     * @param {HTMLElement | null} container
     * @param {string} compareSelector
     * @param {string} currentSelector
     * @param {DOMStringMap} prices - { price, comparePrice } as formatted money
     * @returns {void}
     */
    renderPrice(container, compareSelector, currentSelector, prices) {
      if (!container) return;

      const compare = container.querySelector(compareSelector);
      const current = container.querySelector(currentSelector);
      if (current) current.textContent = prices.price;
      if (compare) {
        compare.textContent = prices.comparePrice;
        compare.hidden = prices.comparePrice === prices.price;
      }
    }

    handleTabClick(event) {
      this.select(event.currentTarget.dataset.switcherTab);
    }

    /**
     * Arrow keys, Home and End move between tabs and select them, per the WAI-ARIA tabs pattern.
     * @param {KeyboardEvent} event
     * @returns {void}
     */
    handleTabKeydown(event) {
      const index = this.tabs.indexOf(event.currentTarget);
      const lastIndex = this.tabs.length - 1;
      const targets = {
        ArrowRight: index === lastIndex ? 0 : index + 1,
        ArrowLeft: index === 0 ? lastIndex : index - 1,
        Home: 0,
        End: lastIndex,
      };
      const nextIndex = targets[event.key];
      if (nextIndex === undefined) return;

      event.preventDefault();
      const nextTab = this.tabs[nextIndex];
      this.select(nextTab.dataset.switcherTab);
      nextTab.focus();
    }
  }

  customElements.define('product-switcher', ProductSwitcher);
}
